// AdminAuthServices.jsx
import { useState, useCallback } from "react";
import API from "../../api/apiAdmin";
import { useNotification } from "../../context/NotificationContext";

export function useAdminAuth() {
  const [admin, setAdmin] = useState(JSON.parse(localStorage.getItem("admin")) || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showNotification } = useNotification();

  const _extract = (res) => (res?.data ? res.data : res);

  // === ADMIN LOGIN ===
  const adminLogin = useCallback(async (email, password, nextPath = null) => {
    setLoading(true);
    setError(null);
    try {
      const payload = { email, password };
      if (nextPath) payload.next = nextPath;

      const res = await API.post("/auth/admin/login/", payload);
      const data = _extract(res);

      if (data.status === "failed") {
        setError(data.message);
        showNotification({
          type: "failed",
          title: "Login Failed",
          message: data.message,
        });
        return data;
      }

      if (data.access_token) {
        localStorage.setItem("admin_token", data.access_token);
      }

      if (data.admin) {
        localStorage.setItem("admin", JSON.stringify(data.admin));
        setAdmin(data.admin);
      }

      const firstName = data.admin?.full_name?.split(" ")[0] || "Admin";
      showNotification({
        type: "success",
        title: "Login Successful",
        message: `Welcome back, ${firstName}!`,
      });

      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Network error or server unavailable.";
      setError(msg);
      showNotification({
        type: "failed",
        title: "Login Failed",
        message: msg,
      });
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  }, [showNotification]);


  const adminLogout = useCallback(async (navigate) => {
    try {
      await API.post("/auth/admin/logout/");
    } catch {
      console.warn("⚠️ Admin logout backend call failed silently");
    } finally {
      localStorage.removeItem("admin");
      localStorage.removeItem("admin_token");
      setAdmin(null);
      if (navigate) navigate("/admin/login");
    }
  }, []);

  // === FORGOT PASSWORD ===
  const forgotPasswordAPI = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/auth/admin/forgot-password/", { email });
      const data = _extract(res);

      if (data.status === "success") {
        showNotification({
          type: "success",
          title: "OTP Sent",
          message: data.message,
        });
      }
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to send OTP.";
      setError(msg);
      showNotification({
        type: "failed",
        title: "OTP Request Failed",
        message: msg,
      });
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // === VERIFY OTP ===
  const verifyOtpAPI = useCallback(async (email, otp) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/auth/admin/verify-otp/", { email, otp });
      const data = _extract(res);
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Invalid OTP.";
      setError(msg);
      showNotification({
        type: "failed",
        title: "Verification Failed",
        message: msg,
      });
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // === RESET PASSWORD ===
  const resetPasswordAPI = useCallback(async (email, otp, password) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/auth/admin/reset-password/", { email, otp, password });
      const data = _extract(res);

      if (data.status === "success") {
        showNotification({
          type: "success",
          title: "Password Reset",
          message: "Your password has been reset successfully!",
        });
      }
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to reset password.";
      setError(msg);
      showNotification({
        type: "failed",
        title: "Error",
        message: msg,
      });
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // === CHANGE PASSWORD ===
  const changePasswordAPI = useCallback(async (passwords) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/auth/admin/change-password", {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      const data = _extract(res);

      if (data.status === "success") {
        showNotification({
          type: "success",
          title: "Password Updated",
          message: data.message,
        });
      }
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update password.";
      setError(msg);
      showNotification({
        type: "failed",
        title: "Update Failed",
        message: msg,
      });
      return { status: "failed", message: msg };
    } finally {
      setLoading(false);
    }
  }, [showNotification]);


  const clearError = useCallback(() => setError(null), []);

  return {
    admin,
    setAdmin,
    loading,
    error,
    adminLogin,
    adminLogout,
    forgotPasswordAPI,
    verifyOtpAPI,
    resetPasswordAPI,
    changePasswordAPI,
    setError,
    clearError,
  };
}
